import type { HelpStep } from '@/features/Vacations/components/VacationHelp/vacationHelp.types';
import type { SiPortalTabId } from '../siPortal.types';

/** Passo do tour do portal: um HelpStep (mesmo formato do tour de Férias) que pode, antes de ser
 * mostrado, preparar a tela — ex.: abrir a lista "Meus equipamentos" pra destacar um botão dela. */
export type PassoTourSi = HelpStep & { antes?: () => void };

/** Clica num elemento marcado com data-tour (ex.: trocar de lista dentro da aba). */
const clicar = (alvo: string) => () => document.querySelector<HTMLElement>(`[data-tour="${alvo}"]`)?.click();

/**
 * Roteiro do tour guiado do Portal do SI, por aba. Cada passo aponta pra um elemento com
 * `data-tour="..."` na tela; passo cujo elemento não está visível (ex.: faixa de ciência sem
 * pendências, lista vazia) é pulado sozinho. Abas que a pessoa não vê nunca entram no tour dela
 * (o "Conhecer o portal" usa só as abas visíveis).
 */
export const SI_PORTAL_TOUR: Partial<Record<SiPortalTabId, PassoTourSi[]>> = {
  inicio: [
    {
      element: 'si-abas',
      title: 'Bem-vindo ao Portal do SI',
      description: 'Cada aba no topo é uma área do portal: informativos, documentos, Guardiões e suas solicitações.',
    },
    {
      element: 'si-faixa-ciencia',
      title: 'Aguardando sua ciência',
      lead: 'Publicações e documentos que pedem "Li e estou ciente".',
      tip: 'As atrasadas aparecem em vermelho. Confirmar fica registrado: o SI sabe quem leu.',
    },
    {
      element: 'si-feed',
      title: 'Informativos',
      description: 'Alertas, dicas e comunicados do SI. Os fixados vêm primeiro; filtre por categoria ou use "Mostrar mais".',
    },
    {
      element: 'si-aguardando',
      title: 'Sua lista de ciências',
      description: 'Atrasadas primeiro, depois o prazo mais próximo e, por fim, as sem prazo.',
      side: 'left',
    },
    {
      element: 'si-atalhos',
      title: 'Atalhos',
      description: 'Acompanhe suas solicitações e veja os equipamentos que estão com você.',
      side: 'left',
    },
    {
      element: 'si-ajuda',
      title: 'Ajuda sempre à mão',
      description: 'Este botão ? fica aqui em todas as abas: refaça o tour da aba ou conheça o portal inteiro quando quiser.',
      side: 'bottom',
      align: 'end',
    },
  ],
  documentos: [
    {
      element: 'si-documentos',
      title: 'Documentos oficiais',
      description: 'Políticas, normas, manuais, onboarding e termos do SI, sempre na versão atual.',
    },
    {
      element: 'si-doc-filtros',
      title: 'Filtros e busca',
      description: 'Filtre por categoria ou pesquise pelo nome do documento.',
    },
    {
      element: 'si-doc-card',
      title: 'Versão e o que mudou',
      description: 'Cada documento mostra a versão e, quando é novo, o que mudou em relação à anterior.',
    },
    {
      element: 'si-doc-acoes',
      title: 'Abrir e confirmar',
      lead: 'Abra e leia o documento (e os anexos) antes de clicar em "Li e estou ciente".',
      tip: 'O botão só libera depois que você abre.',
    },
  ],
  solicitacoes: [
    {
      element: 'si-sol-cartoes',
      title: 'Abrir uma solicitação',
      description: 'Escolha o tipo no cartão: troca, nova alocação, mudança de projeto, devolução, acessos ou periféricos.',
    },
    {
      element: 'si-sol-listas',
      title: 'Suas listas',
      description: 'Minhas solicitações, Da minha equipe (se você for responsável por alguma alocação) e Meus equipamentos.',
    },
    {
      element: 'si-sol-filtros',
      title: 'Filtros',
      description: 'Pesquise e filtre por status e tipo. Por padrão aparecem só as em aberto.',
    },
    {
      element: 'si-sol-lista',
      title: 'Acompanhar',
      description: 'Clique numa solicitação para ver o status.',
    },
    {
      element: 'si-sol-equipamentos',
      title: 'Meus equipamentos',
      description: 'Veja os equipamentos e respectivos termos dos equipamentos que estão inventariados em seu nome.',
    },
    {
      element: 'si-sol-divergencia',
      // Abre a lista "Meus equipamentos" antes de destacar o botão (ele só existe nela).
      antes: clicar('si-sol-equipamentos'),
      title: 'Algo diferente?',
      lead: 'Se a lista não bater com o que está com você, clique em "Informar divergência".',
      tip: 'Serve para um item que você tem e não aparece, um que está no seu nome e não está com você, ou dados errados.',
      side: 'bottom',
      align: 'end',
    },
  ],
  guardioes: [
    {
      element: 'si-gua-jogar',
      title: 'Guardiões dos Dados',
      description: 'Treinamento gamificado de Segurança da Informação, LGPD e IA. Clique em "Jogar agora".',
    },
    {
      element: 'si-gua-status',
      title: 'Seu selo',
      description: 'Conclua com 80% ou mais e ganhe o selo de Guardião ao lado do seu nome, para sempre.',
      side: 'left',
    },
    {
      element: 'si-gua-mural',
      title: 'Mural',
      description: 'Todos que já concluíram com aproveitamento. Sem notas e sem ranking.',
    },
  ],
  controle: [
    {
      element: 'si-ctl-busca',
      title: 'Fila de trabalho',
      description: 'Todas as solicitações para o SI tratar. Pesquise por colaborador, data, equipamento ou tipo.',
    },
    {
      element: 'si-ctl-modos',
      title: 'Formas de ver',
      description: 'Lista, compacta, kanban ou dividida (lista e detalhe lado a lado).',
      side: 'left',
    },
    {
      element: 'si-ctl-filtros',
      title: 'Painel de filtros',
      description: 'Clique nos cartões de status, tipo, projeto, etapa e urgência para recortar a fila.',
    },
    {
      element: 'si-ctl-lista',
      title: 'Próximo passo e execução',
      lead: 'O selo amarelo diz o que falta para concluir cada solicitação.',
      tip: 'Abra uma solicitação para registrar movimentações, termo, fotos e concluir.',
      side: 'top',
    },
  ],
  dashboard: [
    {
      element: 'si-dash',
      title: 'Visão geral',
      description: 'Funil, tempo médio, volume por tipo e pendências da fila. Clique num item para abrir a solicitação.',
    },
  ],
  ativos: [
    {
      element: 'si-ativos-abas',
      title: 'Inventário e estadias',
      description: 'O Inventário tem todos os ativos; Estadias em Cliente mostra o que passou ou está em cliente.',
    },
    {
      element: 'si-ativos-conteudo',
      title: 'Busca, pendências e ficha',
      lead: 'Pesquise por patrimônio, série ou nome e veja as pendências (ex.: "Em uso sem projeto").',
      tip: 'Clique num ativo para abrir a ficha: situação, histórico, anexos e termos.',
      side: 'top',
    },
  ],
  publicacoes: [
    {
      element: 'si-pub-tipos',
      title: 'Posts e Documentos',
      description: 'Escolha o que gerenciar. Quem é do SI vê os posts em modo leitura.',
    },
    {
      element: 'si-pub-status',
      title: 'Status',
      description: 'Filtre por publicados, agendados, rascunhos ou despublicados.',
    },
    {
      element: 'si-pub-nova',
      title: 'Nova publicação',
      description: 'Preencha as informações para realizar a publicação.',
      side: 'left',
    },
    {
      element: 'si-pub-acoes',
      title: 'Ações',
      description: 'Editar, fixar, pedir nova ciência, nova versão, despublicar ou excluir.',
    },
    {
      element: 'si-pub-ciencia',
      title: 'Ciência',
      description: 'Veja quantos confirmaram e abra o relatório com quem falta (e exporte em CSV).',
      side: 'left',
    },
  ],
};
